import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

export default function ChangePasswordPage() {
  const { user, changePassword } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const ruleOk = next.length >= 8 && /[A-Za-z]/.test(next) && /\d/.test(next);
  const matchOk = next === confirm && next.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!matchOk) { setError(t("passwordMismatch")); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success(t("passwordChanged"));
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || t("passwordRule"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex justify-end p-4">
        <LanguageToggle />
      </div>
      <div className="flex flex-1 items-start justify-center px-4 pt-[8vh]">
        <Card className="w-full max-w-sm shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold">{t("changePassword")}</CardTitle>
              {user?.mustChangePassword && (
                <Badge variant="secondary" data-testid="password-required-badge">
                  <ShieldCheck className="mr-1 h-3 w-3" /> Required
                </Badge>
              )}
            </div>
            {user?.mustChangePassword && (
              <CardDescription data-testid="password-change-required-note">
                {t("passwordChangeRequired")}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current">{t("currentPassword")}</Label>
                <Input id="current" type="password" required autoComplete="current-password"
                  data-testid="current-password-input"
                  value={current} onChange={(e) => setCurrent(e.target.value)} className="h-12" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="next">{t("newPassword")}</Label>
                <Input id="next" type="password" required autoComplete="new-password"
                  data-testid="new-password-input"
                  value={next} onChange={(e) => setNext(e.target.value)} className="h-12" />
                <p className={`text-xs ${ruleOk ? "text-emerald-700" : "text-muted-foreground"}`}>
                  {t("passwordRule")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">{t("confirmPassword")}</Label>
                <Input id="confirm" type="password" required autoComplete="new-password"
                  data-testid="confirm-password-input"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" />
              </div>
              {error && <p data-testid="change-password-error" className="text-sm font-medium text-destructive">{error}</p>}
              <Button type="submit" disabled={!ruleOk || !matchOk || busy}
                data-testid="change-password-submit-button" className="h-12 w-full text-base">
                {busy ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("save")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
